import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../networking/client';
import { RootState } from '../store';
import { toast } from 'react-toastify';

export interface IProduct {
  product_id: number;
  product_name: string;
  product_long_description: string;
  product_group_id: number;
  product_group_parent_id?: number;
  product_group_level?: string;
  product_group_name?: string;
  product_group_description?: string;
  product_price?: number;
  product_external_id?: number;
}

export interface IProductCategory {
  product_category_id: number;
  product_category: { text: string; language: string }[];
  level: number;
  product_test_types: string[];
}

export interface ProductLongDescription {
  index: number;
  language: string;
  text: string;
  title: string;
}

export interface IProductDetail {
  claim?: {
    language: string;
    text: string;
  }[];
  product_instruction?: {
    language: string;
    text: string;
    title: string;
    index: number;
  }[];
  name: {
    language: string;
    text: string;
  }[];
  product_id: number;
  long_description?: ProductLongDescription[];
  price: number;
  product_category: { text: string; language: string }[];
  product_group: string;
  product_calendar: any;
  type_of_test: string;
  images?: {
    merchant_product_image_id: number;
    url: string;
    language: string;
    source: string;
  }[];
  languages?: string[][];
  product_add_ons?: {
    product_add_on_id: number;
    product_add_on_name: string;
    product_add_on_price: number;
  }[];
}

export interface IProductCategoriesState {
  status: string;
  productCategories: IProductCategory[];
  productDetails: IProductDetail[];
}

const initialState: IProductCategoriesState = {
  status: 'idle',
  productCategories: [],
  productDetails: [],
};

//* Request to Get All product categories
type GetProductCategories = {
  merchantAccountNumber: string;
  productCategoryId: number;
};
export const fetchProductCategories = createAsyncThunk(
  'productCategories/fetchAll',
  async (fetchProductCategories: GetProductCategories, { dispatch, rejectWithValue }) => {
    try {
      const productCategoriesResponse = await client.get(
        `/core/public/merchant/product_categories?product_category_id=${fetchProductCategories.productCategoryId}&merchant_account_number=${fetchProductCategories.merchantAccountNumber}`
      );
      if (productCategoriesResponse.data) {
        const categoriesResult: IProductCategory[] = productCategoriesResponse.data
          .filter((product: IProductCategory) => product.level === 1)
          .map((category: IProductCategory) => ({
            product_category_id: category.product_category_id,
            level: category.level,
            product_category: category.product_category,
            product_test_types: category.product_test_types,
          }));
        const productsResponse = await client.get(
          `/core/public/merchant/${fetchProductCategories.merchantAccountNumber}/merchant_data`
        );
        if (productsResponse.data) {
          const productsData: IProductDetail[] = productsResponse.data.products;
          //filtering the products and pushing just the needed fields
          const productsResult: IProductDetail[] = [];
          productsData.forEach((product: IProductDetail) => {
            categoriesResult.some((category: IProductCategory) => {
              if (
                category.product_test_types.some((testType: string) =>
                  product.type_of_test.includes(testType)
                )
              ) {
                productsResult.push({
                  claim: product.claim,
                  product_instruction: product.product_instruction,
                  name: product.name,
                  long_description: product.long_description,
                  price: product.price,
                  product_id: product.product_id,
                  product_category: category.product_category,
                  product_calendar: product.product_calendar,
                  type_of_test: product.type_of_test,
                  images: product.images,
                  product_group: product.product_group,
                  languages: product.languages,
                  product_add_ons: product.product_add_ons,
                });
              }
            });
          });
          const healthpointProductsResponse = await client.get(
            '/healthpoint/product-product-groups'
          );
          if (healthpointProductsResponse.data) {
            const healthpointProducts: IProduct[] =
              healthpointProductsResponse.data.productProductGroups;
            const productsFiltered = productsResult.filter((product) =>
              healthpointProducts.find(
                (hpProduct: IProduct) => hpProduct.product_external_id == product.product_id
              )
            );
            return {
              categories: categoriesResult,
              products: productsFiltered,
            };
          }
        }
      }
      throw Error('Error fetching the products');
    } catch (err: any) {
      console.log(err);
      return rejectWithValue({});
    }
  }
);

export const productCategoriesSlice = createSlice({
  name: 'productCategories',
  initialState,
  reducers: {
    resetProductCategories: (_) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProductCategories.pending, (state) => {
        state.status = 'pending';
      })
      .addCase(fetchProductCategories.fulfilled, (state, action) => {
        state.status = 'idle';
        state.productCategories = action.payload.categories;
        state.productDetails = action.payload.products;
      })
      .addCase(fetchProductCategories.rejected, (state, action: any) => {
        toast.error(action?.payload?.error ?? 'Error fetching the products.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        state.status = 'rejected';
      });
  },
});

export const { resetProductCategories } = productCategoriesSlice.actions;

export default productCategoriesSlice.reducer;

export const selectProductCategories = (state: RootState) =>
  state.productCategories.productCategories;
export const selectProductDetails = (state: RootState) => state.productCategories.productDetails;
